import {
  useInfiniteQuery,
  UseInfiniteQueryResult,
  InfiniteData,
  useQuery,
} from "@tanstack/react-query";
import { FullTask } from "../types/FullTask";
import { ActivityFilterType } from "../types/filterOptionsTypes";
import { getUserIdsForTeamId } from "@coworker/app/src/hooks/API/core.service";
import { getActivityLocationsUrl } from "../helpers/getActivityLocationsUrl";
import { ActivityColumns } from "../constants/activityColumns";
import { INTERVAL } from "@coworker/app/src/constants/clientTime";
import {
  fetchAPI,
  TASKS_SERVICE_URL,
} from "@coworker/app/src/hooks/API/tasks.service.helper";
import { Task } from "../../task-management/types/Task";
import { useUID } from "@coworker/app/src/core/auth/useLoggedInUser";

// These are only the properties used for filtering, not ordering
type StrictFilters = Partial<
  Pick<
    ActivityFilterType,
    | "taskType"
    | "createdBy"
    | "assignedTo"
    | "locations"
    | "pickupLocations"
    | "refillLocations"
    | "priority"
    | "page"
    | "sortedBy"
    | "quantityType"
  >
>;

export const ACTIVITY_TASKS_QUERY_KEY = "activityTasks";

const STALE_AFTER = 1000 * 60 * 5;
const getTasksEndpointUrl = (
  filters: StrictFilters & { state: string; pageAfter: string }
) => {
  let url = `tasks?`;
  for (const key in filters) {
    let value = filters[key as keyof StrictFilters];

    if (!value) {
      continue;
    }

    if (Array.isArray(value)) {
      value = value.join(",");
    }
    if (value !== undefined) {
      url += `${key}=${encodeURIComponent(value)}&`;
    }
  }
  return url;
};

export type TaskListItem =
  | { type: "v1"; task_data: FullTask }
  | { type: "v2"; task_data: Task };

type TaskQueryResult = {
  list: TaskListItem[];
  totalCount: number;
  filteredCount: number;
  page?: number;
};

export const useActivityData = (
  filters: StrictFilters,
  queryType: ActivityColumns,
  enableQuery: boolean = true
): {
  openTasks: UseInfiniteQueryResult<
    InfiniteData<TaskQueryResult, unknown>,
    Error
  >;
  inProgressTasks: UseInfiniteQueryResult<
    InfiniteData<TaskQueryResult, unknown>,
    Error
  >;
  completedTasks: UseInfiniteQueryResult<
    InfiniteData<TaskQueryResult, unknown>,
    Error
  >;
  closedTasks: UseInfiniteQueryResult<
    InfiniteData<TaskQueryResult, unknown>,
    Error
  >;
} => {
  const uid = useUID();
  // sorted array of filter values to key the query along with keys
  // it is sorted so the query caching doesn't create multiple queries for the same
  // filter values but in different order
  const filtersIndex = [];
  for (const key in filters) {
    if (filters[key as keyof StrictFilters]) {
      filtersIndex.push(`${key}=${filters[key as keyof StrictFilters]}`);
    }
  }
  filtersIndex.sort();

  const getRelevantUsersIds = async (teamIdOrUserId: string | undefined) => {
    if (!teamIdOrUserId) {
      return "";
    }

    if (uid === teamIdOrUserId) {
      return uid;
    } else {
      const teamsUserIds = await getUserIdsForTeamId(teamIdOrUserId);
      if (teamsUserIds.data.length === 0) {
        return "emptyTeam";
      } else {
        return teamsUserIds.data
          .map((user: { uid: string }) => user.uid)
          .join(",");
      }
    }
  };

  const addTeamFiltersToQueryOptions = async (
    queryOptions: StrictFilters,
    createdBy: string,
    assignedTo: string
  ) => {
    if (createdBy !== "") {
      queryOptions.createdBy = await getRelevantUsersIds(createdBy);
    }
    if (assignedTo !== "") {
      queryOptions.assignedTo = assignedTo;
    }
  };

  const getTasksQueryFunction = async (
    state: string,
    { pageParam = 1 }: { pageParam: string | number }
  ) => {
    let sortedBy = filters.sortedBy!;
    if (state === "completed" || state === "closed") {
      if (filters.sortedBy === "createdAtDesc") {
        sortedBy = "finishedAtDesc";
      } else if (filters.sortedBy === "createdAtAsc") {
        sortedBy = "finishedAtAsc";
      }
    }

    const queryOptions = {
      state: state,
      ...filters,
      page: pageParam as number,
      pageAfter: pageParam as string,
      sortedBy,
    };

    await addTeamFiltersToQueryOptions(
      queryOptions,
      filters.createdBy || "",
      filters.assignedTo || ""
    );

    const url = `${TASKS_SERVICE_URL}/v1/${getTasksEndpointUrl(queryOptions)}`;
    const { result } = await fetchAPI("GET", url);

    return { ...result.data, page: pageParam } as TaskQueryResult;
  };

  const getNextPageParam = (lastPage: TaskQueryResult) => {
    if (lastPage.list && lastPage.list.length > 0) {
      return lastPage.page! + 1;
    }
    return 1;
  };

  const openTasksQueryResult = useInfiniteQuery({
    queryKey: [ACTIVITY_TASKS_QUERY_KEY, "openTasks", ...filtersIndex],
    queryFn: async ({ pageParam }) => {
      return getTasksQueryFunction("open", { pageParam });
    },
    initialPageParam: 1, // Set this to the appropriate initial page parameter
    getNextPageParam: getNextPageParam,
    staleTime: STALE_AFTER,
    enabled: queryType === ActivityColumns.OPEN && enableQuery,
    refetchOnWindowFocus: false,
  });

  const inProgressTasksQueryResult = useInfiniteQuery({
    queryKey: [ACTIVITY_TASKS_QUERY_KEY, "inProgressTasks", ...filtersIndex],
    queryFn: async ({ pageParam }) => {
      return getTasksQueryFunction("in_progress", { pageParam });
    },
    initialPageParam: 1,
    getNextPageParam: getNextPageParam,
    staleTime: STALE_AFTER,
    enabled: queryType === ActivityColumns.IN_PROGRESS && enableQuery,
    refetchOnWindowFocus: false,
  });

  const completedTasksQueryResult = useInfiniteQuery<TaskQueryResult>({
    queryKey: [ACTIVITY_TASKS_QUERY_KEY, "completedTasks", ...filtersIndex],
    queryFn: async ({ pageParam }: any) => {
      return getTasksQueryFunction("completed", { pageParam });
    },
    initialPageParam: 1,
    getNextPageParam: getNextPageParam,
    staleTime: STALE_AFTER,
    enabled: queryType === ActivityColumns.COMPLETED && enableQuery,
    refetchOnWindowFocus: false,
  });

  const closedTasksQueryResult = useInfiniteQuery<TaskQueryResult>({
    queryKey: [ACTIVITY_TASKS_QUERY_KEY, "closedTasks", ...filtersIndex],
    queryFn: async ({ pageParam }: any) => {
      return getTasksQueryFunction("closed", { pageParam });
    },
    initialPageParam: 1,
    getNextPageParam: getNextPageParam,
    staleTime: STALE_AFTER,
    enabled: queryType === ActivityColumns.CLOSED && enableQuery,
    refetchOnWindowFocus: false,
  });

  return {
    openTasks: openTasksQueryResult,
    inProgressTasks: inProgressTasksQueryResult,
    completedTasks: completedTasksQueryResult,
    closedTasks: closedTasksQueryResult,
  };
};

export const useActivityLocations = (
  taskType: string = "all",
  addonLocationType: string,
  enabled = true
) => {
  return useQuery<string[]>({
    queryKey: ["locations", taskType, addonLocationType],
    queryFn: async () => {
      const url = `${TASKS_SERVICE_URL}/v1/${getActivityLocationsUrl(taskType, addonLocationType)}`;
      const { result } = await fetchAPI("GET", url);
      return result.data;
    },
    staleTime: INTERVAL.MINUTE,
    refetchOnWindowFocus: false,
    enabled,
  });
};
